import { useEffect, useState } from "react";
import { collection, doc, getDocs, orderBy, query, updateDoc, where } from "firebase/firestore";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import UsersAction, { USERS_PAGE_SIZE } from "../store/users/action";
import OrderAction from "../store/order/action";
import * as UsersReducer from "../store/users/reducer";
import moment from "moment";
import { User, UserStatusTypes, UserVerifiedContactInfo } from "../models/auth/auth.model";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaUsers, FaEnvelope, FaEnvelopeOpen, FaBox, FaFlag, FaAddressBook } from "react-icons/fa";
import LoadingView from "../components/LoadingView";
import type Order from "../models/order/order.model";
import type { OrderStatus } from "../models/order/order.model";
import { Collections, CollectionsClass } from "../utils/firestore/collections";
import type { ContactMessage } from "../models/contact/contact.model";
import type { SellerReport } from "../models/report/sellerReport.model";
import { handleErrors } from "../utils/firestore/handleErrors";

const statusLabels: Record<UserStatusTypes, string> = {
    pending: "Në pritje",
    approved: "Aprovuar",
    rejected: "Refuzuar",
};

const orderStatusLabels: Record<OrderStatus, string> = {
    created: "Krijuar",
    on_the_way: "Në rrugë",
    delivered: "Dorëzuar",
    canceled: "Anuluar",
    returned: "Kthyer",
};

const statusStyles: Record<UserStatusTypes, string> = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
};

const orderStatusStyles: Record<OrderStatus, string> = {
    created: "bg-amber-100 text-amber-800",
    on_the_way: "bg-blue-100 text-blue-800",
    delivered: "bg-green-100 text-green-800",
    canceled: "bg-red-100 text-red-800",
    returned: "bg-gray-100 text-gray-800",
};

const ORDER_STATUSES: OrderStatus[] = ["created", "on_the_way", "delivered", "canceled", "returned"];

type AdminSection =
    | "users"
    | "contacts-pending"
    | "inbox-pending"
    | "inbox-responded"
    | "reports-pending"
    | "reports-responded"
    | "orders";

const VERIFIED_CONTACT_KEYS = ["instagram", "facebook", "tiktok", "phone"] as const;

const pendingContactChannelLabels: Record<(typeof VERIFIED_CONTACT_KEYS)[number], string> = {
    instagram: "Instagram",
    facebook: "Facebook",
    tiktok: "TikTok",
    phone: "Telefon",
};

function pendingVerifiedContactLabels(v: UserVerifiedContactInfo | undefined): string[] {
    if (!v) return [];
    return VERIFIED_CONTACT_KEYS.filter((k) => v[k] === "pending").map((k) => pendingContactChannelLabels[k]);
}

const AdminHome = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((s) => s.auth);
    const { isLoading, users, docsHistory, userByEmail, updateUserStatusSuccess, error } = useAppSelector((s) => s.users);
    const { adminOrders, adminOrdersLoading, adminOrdersError } = useAppSelector((s) => s.order);

    const [selectedStatus, setSelectedStatus] = useState<UserStatusTypes>("pending");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [searchEmail, setSearchEmail] = useState("");
    const [selectedOrderStatus, setSelectedOrderStatus] = useState<OrderStatus>("created");
    const [activeSection, setActiveSection] = useState<AdminSection>("users");
    const [inboxMessages, setInboxMessages] = useState<(ContactMessage & { id: string })[]>([]);
    const [inboxRespondedMessages, setInboxRespondedMessages] = useState<(ContactMessage & { id: string })[]>([]);
    const [inboxLoading, setInboxLoading] = useState(false);
    const [inboxError, setInboxError] = useState("");
    const [inboxUpdatingId, setInboxUpdatingId] = useState<string | null>(null);
    const [messageDetailModal, setMessageDetailModal] = useState<ContactMessage & { id: string } | null>(null);
    const [reportsPending, setReportsPending] = useState<(SellerReport & { id: string })[]>([]);
    const [reportsResponded, setReportsResponded] = useState<(SellerReport & { id: string })[]>([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsError, setReportsError] = useState("");
    const [reportsUpdatingId, setReportsUpdatingId] = useState<string | null>(null);
    const [reportDetailModal, setReportDetailModal] = useState<SellerReport & { id: string } | null>(null);
    const [pendingContactsUsers, setPendingContactsUsers] = useState<User[]>([]);
    const [pendingContactsLoading, setPendingContactsLoading] = useState(false);
    const [pendingContactsError, setPendingContactsError] = useState("");
    const [addPostModalOpen, setAddPostModalOpen] = useState(false);
    const [addPostUser, setAddPostUser] = useState<User | null>(null);
    const [addPostDocId, setAddPostDocId] = useState("");
    const [addPostLoading, setAddPostLoading] = useState(false);
    const [addPostError, setAddPostError] = useState("");

    useEffect(() => {
        if (activeSection !== "users") return;
        dispatch(UsersReducer.setDocsHistory([null]));
        UsersAction.getUsers(selectedStatus, "next", [null], 1, dispatch);
        setCurrentPage(1);
    }, [activeSection, selectedStatus, dispatch]);

    useEffect(() => {
        if (!updateUserStatusSuccess || activeSection !== "users") return;
        UsersAction.getUsers(selectedStatus, "next", docsHistory, currentPage, dispatch);
        UsersAction.clearState(dispatch);
    }, [updateUserStatusSuccess, activeSection]);

    useEffect(() => {
        if (activeSection !== "orders") return;
        OrderAction.getOrdersByStatus(dispatch, selectedOrderStatus);
    }, [activeSection, selectedOrderStatus, dispatch]);

    useEffect(() => {
        if (activeSection !== "inbox-pending") return;
        setInboxError("");
        setInboxLoading(true);
        const coll = collection(CollectionsClass.instance, Collections.CONTACT_MESSAGES);
        const q = query(
            coll,
            where("status", "==", "pending"),
            orderBy("created_at", "desc")
        );
        getDocs(q)
            .then((snap) => {
                const list = snap.docs.map((d) => ({ ...d.data(), id: d.id } as ContactMessage & { id: string }));
                setInboxMessages(list);
            })
            .catch((e) => setInboxError(handleErrors(e)))
            .finally(() => setInboxLoading(false));
    }, [activeSection]);

    useEffect(() => {
        if (activeSection !== "inbox-responded") return;
        setInboxError("");
        setInboxLoading(true);
        const coll = collection(CollectionsClass.instance, Collections.CONTACT_MESSAGES);
        const q = query(
            coll,
            where("status", "==", "responded"),
            orderBy("created_at", "desc")
        );
        getDocs(q)
            .then((snap) => {
                const list = snap.docs.map((d) => ({ ...d.data(), id: d.id } as ContactMessage & { id: string }));
                setInboxRespondedMessages(list);
            })
            .catch((e) => setInboxError(handleErrors(e)))
            .finally(() => setInboxLoading(false));
    }, [activeSection]);

    useEffect(() => {
        if (activeSection !== "reports-pending") return;
        setReportsError("");
        setReportsLoading(true);
        const coll = collection(CollectionsClass.instance, Collections.SELLER_REPORTS);
        const q = query(
            coll,
            where("status", "==", "pending"),
            orderBy("created_at", "desc")
        );
        getDocs(q)
            .then((snap) => {
                const list = snap.docs.map((d) => ({ ...d.data(), id: d.id } as SellerReport & { id: string }));
                setReportsPending(list);
            })
            .catch((e) => setReportsError(handleErrors(e)))
            .finally(() => setReportsLoading(false));
    }, [activeSection]);

    useEffect(() => {
        if (activeSection !== "reports-responded") return;
        setReportsError("");
        setReportsLoading(true);
        const coll = collection(CollectionsClass.instance, Collections.SELLER_REPORTS);
        const q = query(
            coll,
            where("status", "==", "responded"),
            orderBy("created_at", "desc")
        );
        getDocs(q)
            .then((snap) => {
                const list = snap.docs.map((d) => ({ ...d.data(), id: d.id } as SellerReport & { id: string }));
                setReportsResponded(list);
            })
            .catch((e) => setReportsError(handleErrors(e)))
            .finally(() => setReportsLoading(false));
    }, [activeSection]);

    useEffect(() => {
        if (activeSection !== "contacts-pending") return;
        setPendingContactsError("");
        setPendingContactsLoading(true);
        const coll = collection(CollectionsClass.instance, Collections.USERS);
        Promise.all(
            VERIFIED_CONTACT_KEYS.map((key) =>
                getDocs(query(coll, where(`verifiedContacts.${key}`, "==", "pending")))
            )
        )
            .then((snaps) => {
                const byId = new Map<string, User>();
                for (const snap of snaps) {
                    snap.docs.forEach((d) => {
                        const u = d.data() as User;
                        if (u?.id) byId.set(u.id, u);
                    });
                }
                const list = Array.from(byId.values()).sort((a, b) => (a.created_at ?? 0) - (b.created_at ?? 0));
                setPendingContactsUsers(list);
            })
            .catch((e) => setPendingContactsError(handleErrors(e)))
            .finally(() => setPendingContactsLoading(false));
    }, [activeSection]);

    const handleMarkReportResponded = (reportId: string) => {
        setReportsUpdatingId(reportId);
        const docRef = doc(CollectionsClass.instance, Collections.SELLER_REPORTS, reportId);
        updateDoc(docRef, { status: "responded" })
            .then(() => setReportsPending((prev) => prev.filter((r) => r.id !== reportId)))
            .catch((e) => setReportsError(handleErrors(e)))
            .finally(() => setReportsUpdatingId(null));
    };

    const handleNextPage = () => {
        UsersAction.getUsers(selectedStatus, "next", docsHistory, currentPage + 1, dispatch);
        setCurrentPage((p) => p + 1);
    };

    const handlePreviousPage = () => {
        UsersAction.getUsers(selectedStatus, "prev", docsHistory, currentPage - 1, dispatch);
        setCurrentPage((p) => Math.max(1, p - 1));
    };

    const handleUserRowClick = (u: User) => {
        navigate("/user", { state: { user: u } });
        UsersAction.clearState(dispatch);
    };

    const handleApprove = (u: User, e: React.MouseEvent) => {
        e.stopPropagation();
        UsersAction.setUserStatus(u.id, "approved", dispatch);
    };

    const handleReject = (u: User, e: React.MouseEvent) => {
        e.stopPropagation();
        UsersAction.setUserStatus(u.id, "rejected", dispatch);
    };

    const handleOrderStatusChange = (order: Order, newStatus: OrderStatus) => {
        OrderAction.updateOrderStatus(order, newStatus, dispatch, selectedOrderStatus);
    };

    const handleMarkResponded = (msgId: string) => {
        setInboxUpdatingId(msgId);
        const docRef = doc(CollectionsClass.instance, Collections.CONTACT_MESSAGES, msgId);
        updateDoc(docRef, { status: "responded" })
            .then(() => {
                setInboxMessages((prev) => prev.filter((m) => m.id !== msgId));
            })
            .catch((e) => setInboxError(handleErrors(e)))
            .finally(() => setInboxUpdatingId(null));
    };

    const openAddPostModal = (u: User) => {
        setAddPostUser(u);
        setAddPostDocId("");
        setAddPostError("");
        setAddPostModalOpen(true);
    };

    const closeAddPostModal = () => {
        setAddPostModalOpen(false);
        setAddPostUser(null);
        setAddPostDocId("");
        setAddPostError("");
        setAddPostLoading(false);
    };

    const handleAddPostSubmit = () => {
        if (!addPostUser) return;
        if (!addPostDocId.trim()) {
            setAddPostError("Post doc id is required");
            return;
        }
        setAddPostLoading(true);
        UsersAction.addExistingPostToUser(addPostUser.id, addPostDocId.trim(), dispatch)
            .then(() => {
                UsersAction.getUsers(selectedStatus, "next", docsHistory, currentPage, dispatch);
                setAddPostLoading(false);
                closeAddPostModal();
            })
            .catch(() => setAddPostLoading(false));
    };

    const navItemClass = (section: AdminSection) =>
        `flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left font-medium text-sm transition ${activeSection === section ? "bg-[#704e37] text-white" : "text-gray-700 hover:bg-[#704e37]/10 hover:text-[#704e37]"}`;

    const navSubItemClass = (section: AdminSection) =>
        `flex items-center gap-3 w-full pl-10 pr-4 py-2.5 rounded-lg text-left text-sm transition ${activeSection === section ? "bg-[#704e37]/15 text-[#704e37] font-medium" : "text-gray-600 hover:bg-gray-100"}`;

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />

            <div className="flex max-w-7xl mx-auto px-4 sm:px-6 py-8 gap-8">
                {/* Left sidebar – categories */}
                <aside className="w-56 shrink-0">
                    <nav className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-2">
                            <button
                                type="button"
                                onClick={() => setActiveSection("users")}
                                className={navItemClass("users")}
                            >
                                <FaUsers className="text-lg shrink-0" />
                                Përdoruesit
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveSection("contacts-pending")}
                                className={navSubItemClass("contacts-pending")}
                            >
                                <FaAddressBook className="shrink-0" />
                                Kontakte në pritje
                            </button>
                            <div className="mt-1">
                                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Mesazhet</div>
                                <button
                                    type="button"
                                    onClick={() => setActiveSection("inbox-pending")}
                                    className={navSubItemClass("inbox-pending")}
                                >
                                    <FaEnvelope className="shrink-0" />
                                    Në pritje
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSection("inbox-responded")}
                                    className={navSubItemClass("inbox-responded")}
                                >
                                    <FaEnvelopeOpen className="shrink-0" />
                                    Të kompletuara
                                </button>
                            </div>
                            <div className="mt-1">
                                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Raportet</div>
                                <button
                                    type="button"
                                    onClick={() => setActiveSection("reports-pending")}
                                    className={navSubItemClass("reports-pending")}
                                >
                                    <FaFlag className="shrink-0" />
                                    Në pritje
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSection("reports-responded")}
                                    className={navSubItemClass("reports-responded")}
                                >
                                    <FaFlag className="shrink-0" />
                                    U përgjigjur
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setActiveSection("orders")}
                                className={navItemClass("orders")}
                            >
                                <FaBox className="text-lg shrink-0" />
                                Porositë
                            </button>
                        </div>
                    </nav>
                </aside>

                {/* Main content */}
                <main className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold text-[#704e37] mb-2">Paneli i administratorit</h1>
                    <p className="text-gray-600 mb-6">{user?.first_name} {user?.last_name}</p>

                    {activeSection === "users" && (
                        <section>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {(["pending", "approved", "rejected"] as UserStatusTypes[]).map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => setSelectedStatus(status)}
                                        className={`px-4 py-2 rounded-lg font-medium text-sm transition ${selectedStatus === status ? "bg-[#704e37] text-white" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"}`}
                                    >
                                        {status === "pending" ? "Në pritje" : status === "approved" ? "Aprovuar" : "Refuzuar"}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setSearchModalOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                                >
                                    <FaSearch /> Kërko
                                </button>
                            </div>
                            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
                            {isLoading && <div className="flex justify-center py-8"><LoadingView size={40} /></div>}

                            {!isLoading && users && users.length > 0 && selectedStatus !== "pending" && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emri</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statusi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email i verifikuar</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Krijuar</th>
                                                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veprime</th> */}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {users.map((u) => (
                                                    <tr
                                                        key={u.id}
                                                        onClick={() => handleUserRowClick(u)}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.first_name} {u.last_name}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusStyles[u.status]}`}>
                                                                {statusLabels[u.status]}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${u.verified ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                                                                {u.verified ? "Po" : "Jo"}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{moment(u.created_at).format("DD/MM/YYYY")}</td>
                                                        {/* <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                onClick={() => openAddPostModal(u)}
                                                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
                                                            >
                                                                Shto post
                                                            </button>
                                                        </td> */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t">
                                        <button
                                            type="button"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-50 text-sm font-medium"
                                        >
                                            Prapa
                                        </button>
                                        <span className="text-sm text-gray-600">Faqja {currentPage}</span>
                                        <button
                                            type="button"
                                            onClick={handleNextPage}
                                            disabled={!users || users.length < USERS_PAGE_SIZE}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-50 text-sm font-medium"
                                        >
                                            Para
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!isLoading && users && selectedStatus === "pending" && (() => {
                                const verifiedPending = (users ?? []).filter((u) => u.verified === true);
                                const unverifiedPending = (users ?? []).filter((u) => u.verified !== true);
                                const renderPendingTable = (list: User[], title: string) => (
                                    <div className="mb-8">
                                        <h3 className="text-base font-semibold text-gray-700 mb-3">{title}</h3>
                                        {list.length === 0 ? (
                                            <p className="text-gray-500 text-sm py-4">Nuk ka përdorues.</p>
                                        ) : (
                                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emri</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statusi</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email i verifikuar</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Krijuar</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veprime</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {list.map((u) => (
                                                                <tr
                                                                    key={u.id}
                                                                    onClick={() => handleUserRowClick(u)}
                                                                    className="hover:bg-gray-50 cursor-pointer"
                                                                >
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.first_name} {u.last_name}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusStyles[u.status]}`}>
                                                                            {statusLabels[u.status]}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${u.verified ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                                                                            {u.verified ? "Po" : "Jo"}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-500">{moment(u.created_at).format("DD/MM/YYYY")}</td>
                                                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => handleApprove(u, e)}
                                                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                                                                            >
                                                                                Aprovo
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => handleReject(u, e)}
                                                                                className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                                                                            >
                                                                                Refuzo
                                                                            </button>
                                                                                {/* <button
                                                                                    type="button"
                                                                                    onClick={() => openAddPostModal(u)}
                                                                                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
                                                                                >
                                                                                    Shto post
                                                                                </button> */}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                                return (
                                    <>
                                        {renderPendingTable(verifiedPending, "Në pritje – Email i verifikuar")}
                                        {renderPendingTable(unverifiedPending, "Në pritje – Email i pa verifikuar")}
                                        <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <button
                                                type="button"
                                                onClick={handlePreviousPage}
                                                disabled={currentPage === 1}
                                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-50 text-sm font-medium"
                                            >
                                                Prapa
                                            </button>
                                            <span className="text-sm text-gray-600">Faqja {currentPage}</span>
                                            <button
                                                type="button"
                                                onClick={handleNextPage}
                                                disabled={!users || users.length < USERS_PAGE_SIZE}
                                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-50 text-sm font-medium"
                                            >
                                                Para
                                            </button>
                                        </div>
                                    </>
                                );
                            })()}

                            {!isLoading && (!users || users.length === 0) && (
                                <p className="text-gray-500 py-8 text-center">Nuk ka përdorues për këtë status.</p>
                            )}
                        </section>
                    )}

                    {activeSection === "contacts-pending" && (
                        <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-2">Kontakte në pritje</h2>
                            <p className="text-gray-500 text-sm mb-4">
                                Përdoruesit që kanë të paktën një rrjet social ose telefon me status &quot;Në pritje&quot; për verifikim.
                            </p>
                            {pendingContactsError && (
                                <p className="text-red-600 text-sm mb-2">{pendingContactsError}</p>
                            )}
                            {pendingContactsLoading && (
                                <div className="flex justify-center py-8">
                                    <LoadingView size={40} />
                                </div>
                            )}
                            {!pendingContactsLoading && pendingContactsUsers.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Emri
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Email
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Statusi i llogarisë
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Në pritje
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Krijuar
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {pendingContactsUsers.map((u) => {
                                                    const channels = pendingVerifiedContactLabels(u.verifiedContacts);
                                                    return (
                                                        <tr
                                                            key={u.id}
                                                            onClick={() => handleUserRowClick(u)}
                                                            className="hover:bg-gray-50 cursor-pointer"
                                                        >
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                                {u.first_name} {u.last_name}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                                                            <td className="px-4 py-3">
                                                                <span
                                                                    className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusStyles[u.status]}`}
                                                                >
                                                                    {statusLabels[u.status]}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {channels.map((label) => (
                                                                        <span
                                                                            key={label}
                                                                            className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-900"
                                                                        >
                                                                            {label}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                                {moment(u.created_at).format("DD/MM/YYYY")}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {!pendingContactsLoading && pendingContactsUsers.length === 0 && !pendingContactsError && (
                                <p className="text-gray-500 py-8 text-center">
                                    Nuk ka kontakte në pritje verifikimi.
                                </p>
                            )}
                        </section>
                    )}

                    {activeSection === "inbox-pending" && (
                        <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">Mesazhe në pritje</h2>
                            <p className="text-gray-500 text-sm mb-4">Përgjigjuni nga Gmail, pastaj shënoni si "U përgjigj".</p>
                            {inboxError && <p className="text-red-600 text-sm mb-2">{inboxError}</p>}
                            {inboxLoading && <div className="flex justify-center py-8"><LoadingView size={40} /></div>}
                            {!inboxLoading && inboxMessages.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titulli</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mesazhi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veprimi</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {inboxMessages.map((msg) => (
                                                    <tr
                                                        key={msg.id}
                                                        onClick={() => setMessageDetailModal(msg)}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{msg.title ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{msg.email}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={msg.message}>{msg.message}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{moment(msg.created_at).format("DD/MM/YYYY HH:mm")}</td>
                                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMarkResponded(msg.id)}
                                                                disabled={inboxUpdatingId === msg.id}
                                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                                            >
                                                                {inboxUpdatingId === msg.id ? "Duke ruajtur..." : "Kompleto si përgjigjur"}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {!inboxLoading && inboxMessages.length === 0 && (
                                <p className="text-gray-500 py-8 text-center">Nuk ka mesazhe në pritje.</p>
                            )}
                        </section>
                    )}

                    {activeSection === "inbox-responded" && (
                        <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">Mesazhe u përgjigjur</h2>
                            {inboxError && <p className="text-red-600 text-sm mb-2">{inboxError}</p>}
                            {inboxLoading && <div className="flex justify-center py-8"><LoadingView size={40} /></div>}
                            {!inboxLoading && inboxRespondedMessages.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titulli</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mesazhi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {inboxRespondedMessages.map((msg) => (
                                                    <tr
                                                        key={msg.id}
                                                        onClick={() => setMessageDetailModal(msg)}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{msg.title ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{msg.email}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate" title={msg.message}>{msg.message}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{moment(msg.created_at).format("DD/MM/YYYY HH:mm")}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {!inboxLoading && inboxRespondedMessages.length === 0 && (
                                <p className="text-gray-500 py-8 text-center">Nuk ka mesazhe u përgjigjur.</p>
                            )}
                        </section>
                    )}

                    {activeSection === "orders" && (
                        <section>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {ORDER_STATUSES.map((status) => (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => setSelectedOrderStatus(status)}
                                        className={`px-4 py-2 rounded-lg font-medium text-sm transition ${selectedOrderStatus === status ? "bg-[#704e37] text-white" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"}`}
                                    >
                                        {orderStatusLabels[status]}
                                    </button>
                                ))}
                            </div>
                            {adminOrdersError && <p className="text-red-600 text-sm mb-2">{adminOrdersError}</p>}
                            {adminOrdersLoading && <div className="flex justify-center py-8"><LoadingView size={40} /></div>}

                            {!adminOrdersLoading && adminOrders && adminOrders.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produkti</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nga</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statusi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ndrysho</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {adminOrders.map((order) => (
                                                    <tr
                                                        key={order._docId ?? `${order.id}-${order.sender?.id}`}
                                                        onClick={() => navigate("/order/detail", { state: { order } })}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 truncate max-w-[160px]">{order.product?.name ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{order.senders_data?.fullname ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-800">{order.total_price} €</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{order.created_at}</td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${orderStatusStyles[order.status]}`}>
                                                                {orderStatusLabels[order.status]}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                            <select
                                                                value={order.status}
                                                                onChange={(e) => handleOrderStatusChange(order, e.target.value as OrderStatus)}
                                                                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-800 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37]"
                                                            >
                                                                {(Object.keys(orderStatusLabels) as OrderStatus[]).map((s) => (
                                                                    <option key={s} value={s}>{orderStatusLabels[s]}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {!adminOrdersLoading && (!adminOrders || adminOrders.length === 0) && (
                                <p className="text-gray-500 py-8 text-center">Nuk ka porosi.</p>
                            )}
                        </section>
                    )}

                    {activeSection === "reports-pending" && (
                        <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">Raportet e shitësve – Në pritje</h2>
                            {reportsError && <p className="text-red-600 text-sm mb-2">{reportsError}</p>}
                            {reportsLoading && <div className="flex justify-center py-8"><LoadingView size={40} /></div>}
                            {!reportsLoading && reportsPending.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shitësi (email)</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Raportuesi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titulli</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mesazhi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veprimi</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {reportsPending.map((r) => (
                                                    <tr
                                                        key={r.id}
                                                        onClick={() => setReportDetailModal(r)}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3 text-sm text-gray-600">{r.seller_email ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{r.email}</td>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{r.title ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={r.message}>{r.message}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{moment(r.created_at).format("DD/MM/YYYY HH:mm")}</td>
                                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMarkReportResponded(r.id)}
                                                                disabled={reportsUpdatingId === r.id}
                                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                                            >
                                                                {reportsUpdatingId === r.id ? "Duke ruajtur..." : "Shëno si u përgjigj"}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {!reportsLoading && reportsPending.length === 0 && (
                                <p className="text-gray-500 py-8 text-center">Nuk ka raporte në pritje.</p>
                            )}
                        </section>
                    )}

                    {activeSection === "reports-responded" && (
                        <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">Raportet e shitësve – U përgjigjur</h2>
                            {reportsError && <p className="text-red-600 text-sm mb-2">{reportsError}</p>}
                            {reportsLoading && <div className="flex justify-center py-8"><LoadingView size={40} /></div>}
                            {!reportsLoading && reportsResponded.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shitësi (email)</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Raportuesi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titulli</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mesazhi</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {reportsResponded.map((r) => (
                                                    <tr
                                                        key={r.id}
                                                        onClick={() => setReportDetailModal(r)}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3 text-sm text-gray-600">{r.seller_email ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">{r.email}</td>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{r.title ?? "—"}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate" title={r.message}>{r.message}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{moment(r.created_at).format("DD/MM/YYYY HH:mm")}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {!reportsLoading && reportsResponded.length === 0 && (
                                <p className="text-gray-500 py-8 text-center">Nuk ka raporte u përgjigjur.</p>
                            )}
                        </section>
                    )}
                </main>
            </div>

            {/* Message detail modal */}
            {messageDetailModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setMessageDetailModal(null)}>
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Detajet e mesazhit</h3>
                        <dl className="space-y-3">
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Titulli</dt>
                                <dd className="mt-0.5 text-gray-900 font-medium">{messageDetailModal.title ?? "—"}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email i dërguesit</dt>
                                <dd className="mt-0.5 text-gray-900">{messageDetailModal.email}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Data</dt>
                                <dd className="mt-0.5 text-gray-600">{moment(messageDetailModal.created_at).format("DD/MM/YYYY HH:mm")}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Mesazhi</dt>
                                <dd className="mt-0.5 text-gray-700 whitespace-pre-wrap break-words">{messageDetailModal.message || "—"}</dd>
                            </div>
                        </dl>
                        {activeSection === "inbox-pending" && (
                            <div className="mt-6 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleMarkResponded(messageDetailModal.id);
                                        setMessageDetailModal(null);
                                    }}
                                    disabled={inboxUpdatingId === messageDetailModal.id}
                                    className="w-full py-2.5 rounded-lg bg-green-600 text-white font-medium text-sm hover:bg-green-700 disabled:opacity-50"
                                >
                                    {inboxUpdatingId === messageDetailModal.id ? "Duke ruajtur..." : "Kompleto si përgjigjur"}
                                </button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setMessageDetailModal(null)}
                            className="mt-4 w-full py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                        >
                            Mbyll
                        </button>
                    </div>
                </div>
            )}

            {/* Report detail modal */}
            {reportDetailModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setReportDetailModal(null)}>
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Detajet e raportit</h3>
                        <dl className="space-y-3">
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Shitësi i raportuar (email)</dt>
                                <dd className="mt-0.5 text-gray-900">{reportDetailModal.seller_email ?? "—"}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">ID e shitësit</dt>
                                <dd className="mt-0.5 text-gray-900 font-mono text-sm">{reportDetailModal.seller_id ?? "—"}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email i raportuesit</dt>
                                <dd className="mt-0.5 text-gray-900">{reportDetailModal.email}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Titulli</dt>
                                <dd className="mt-0.5 text-gray-900 font-medium">{reportDetailModal.title ?? "—"}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Data</dt>
                                <dd className="mt-0.5 text-gray-600">{moment(reportDetailModal.created_at).format("DD/MM/YYYY HH:mm")}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Mesazhi</dt>
                                <dd className="mt-0.5 text-gray-700 whitespace-pre-wrap break-words">{reportDetailModal.message || "—"}</dd>
                            </div>
                        </dl>
                        {activeSection === "reports-pending" && (
                            <div className="mt-6 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleMarkReportResponded(reportDetailModal.id);
                                        setReportDetailModal(null);
                                    }}
                                    disabled={reportsUpdatingId === reportDetailModal.id}
                                    className="w-full py-2.5 rounded-lg bg-green-600 text-white font-medium text-sm hover:bg-green-700 disabled:opacity-50"
                                >
                                    {reportsUpdatingId === reportDetailModal.id ? "Duke ruajtur..." : "Shëno si u përgjigj"}
                                </button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setReportDetailModal(null)}
                            className="mt-4 w-full py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                        >
                            Mbyll
                        </button>
                    </div>
                </div>
            )}

            {/* Search user modal */}
            {searchModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSearchModalOpen(false)}>
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Kërko përdoruesin sipas email-it</h3>
                        <input
                            type="email"
                            placeholder="Shkruani email-in"
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 mb-4 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                        />
                        {userByEmail && (
                            <button
                                type="button"
                                onClick={() => handleUserRowClick(userByEmail)}
                                className="w-full flex justify-between items-center p-3 border border-gray-200 rounded-lg mb-4 hover:bg-gray-50 text-left"
                            >
                                <div>
                                    <p className="font-medium text-gray-900">{userByEmail.first_name} {userByEmail.last_name}</p>
                                    <p className="text-sm text-gray-500">{userByEmail.email}</p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[userByEmail.status]}`}>
                                    {statusLabels[userByEmail.status]}
                                </span>
                            </button>
                        )}
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => UsersAction.searchByEmail(searchEmail, dispatch)}
                                className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90 disabled:opacity-50"
                            >
                                Kërko
                            </button>
                            <button
                                type="button"
                                onClick={() => { setSearchModalOpen(false); UsersAction.clearState(dispatch); }}
                                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                            >
                                Mbyll
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Add post to user modal (admin) */}
            {addPostModalOpen && addPostUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeAddPostModal}>
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Shto post në llogarinë e përdoruesit</h3>
                        <p className="text-sm text-gray-600 mb-3">Përdorues: {addPostUser.first_name} {addPostUser.last_name} — {addPostUser.email}</p>
                        {addPostError && <p className="text-red-600 text-sm mb-2">{addPostError}</p>}
                        <input
                            type="text"
                            placeholder="Post document id (e.g. post_userid_postid)"
                            value={addPostDocId}
                            onChange={(e) => setAddPostDocId(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 mb-4 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                        />
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={addPostLoading}
                                onClick={handleAddPostSubmit}
                                className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90 disabled:opacity-50"
                            >
                                {addPostLoading ? "Shtohet..." : "Shto post"}
                            </button>
                            <button
                                type="button"
                                onClick={closeAddPostModal}
                                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                            >
                                Anulo
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminHome;
